// ONE DISCIPLINE PICKER, CONTRACTORS UNDER EACH DISCIPLINE.
//
// Owner, 2026-10-15: "the dicipline, should list each contractor by
// discipline". Used by bulk "Set fields" on the issue list, the "Add
// multiple issues" fill panel and Discipline column, and the issue page's
// LINKED TO block - the options come from disciplineContractorOptions.js,
// which says what each pick means.
//
//   <DisciplineContractorPicker
//     disciplines={companyDisciplines} roster={roster}
//     value={{ discipline, responsibleCompanyId }}
//     onChange={(pick) => ...}   // { discipline, responsibleCompanyId? } | null on clear
//     allowNone                  // offer "— none —" (clears the discipline)
//     noRosterReason="..."       // why no contractors can be listed, in words
//   />
//
// onChange gets responsibleCompanyId ONLY when a contractor was picked: a
// bare discipline leaves the issue's contractor as it is, and the caller
// can tell the two apart by the key being there or not.
import React, { useMemo } from "react";
import { Select } from "antd";
import {
  disciplineContractorOptions, parsePick, pickValue, filterPick, NONE_VALUE,
} from "./disciplineContractorOptions";
import { PartnerWithTrades, TradeWithPartners } from "components/Partners/PartnerTradeLabel";

// Two lines (owner, 2026-10-16): a discipline with its partners beneath, a
// partner with its trades beneath. The closed box keeps `display`.
function renderPick(opt) {
  const d = (opt && opt.data) || {};
  if (Array.isArray(d.partners)) return <TradeWithPartners label={d.label} partners={d.partners} />;
  if (Array.isArray(d.trades)) return <PartnerWithTrades name={d.label} trades={d.trades} />;
  return d.label;
}

export default function DisciplineContractorPicker({
  disciplines = [], roster = [], value = null, onChange, allowNone = false,
  placeholder = "Discipline / contractor", noRosterReason = "", size, style, disabled = false,
  "aria-label": ariaLabel = "discipline",
}) {
  const { groups, untraded, contractorCount } = useMemo(
    () => disciplineContractorOptions({ disciplines, roster, current: value }),
    [disciplines, roster, value && value.discipline] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const options = allowNone
    ? [{ value: NONE_VALUE, label: "— none —", display: "— none —", search: "none" }, ...groups]
    : groups;
  // "" with allowNone is the deliberate "— none —" of a bulk edit; without
  // it, an issue with no discipline simply shows the placeholder.
  const selected = allowNone && value && value.discipline === ""
    ? NONE_VALUE
    : pickValue(groups, value || {});

  // SAID, NOT HIDDEN: why the list holds fewer contractors than the project has.
  const notes = [];
  if (noRosterReason) notes.push(noRosterReason);
  else if (!contractorCount && !untraded) notes.push("No partners on this project yet");
  if (untraded) {
    notes.push(`${untraded} partner${untraded === 1 ? " has" : "s have"} no discipline on this project, so ${untraded === 1 ? "it is" : "they are"} not listed - set their trades under Partners`);
  }
  return (
    <Select
      allowClear showSearch size={size} disabled={disabled}
      style={{ width: "100%", ...(style || {}) }}
      placeholder={placeholder}
      value={selected}
      options={options}
      optionLabelProp="display"
      optionRender={renderPick}
      filterOption={filterPick}
      aria-label={ariaLabel}
      notFoundContent={groups.length ? "No match" : "No disciplines yet - add them under Doc Control"}
      dropdownRender={(menu) => (
        <>
          {menu}
          {notes.length ? (
            <div data-testid="dc-picker-note" style={{ fontSize: 11, color: "#6B7280", padding: "6px 10px", borderTop: "1px solid #f0f0f0" }}>
              {notes.join(". ")}
            </div>
          ) : null}
        </>
      )}
      onChange={(v) => { if (onChange) onChange(v === undefined ? null : parsePick(v)); }}
    />
  );
}
